/**
 * Authenticated fetches for blobs the API now proxies (MDRS-503).
 *
 * The backend used to hand the frontend Azure SAS URLs for clinical documents,
 * report drafts and finished reports. A SAS is a standalone bearer credential
 * that goes straight to Azure, so the ownership check ran once when the link
 * was minted and never on the fetch — anyone holding the URL could read the
 * document until it expired.
 *
 * Those blobs are now served by owner-scoped API endpoints that re-authorise
 * every request. The trade-off is that the browser can no longer point an
 * `<img src>`, `<iframe src>` or `<a download>` at the URL directly: none of
 * those attach the `Authorization` header. So the bytes are fetched through
 * `apiClient` (which owns the token and the 401-refresh retry) and handed to
 * the viewer as an object URL instead.
 *
 * As a side benefit this removes the Azure-CORS problem the old preview path
 * worked around — these are same-origin API requests, not cross-origin blob
 * ones.
 */
import { apiClient } from "./apiClient";

/** Shape hey-api returns: exactly one of `data` / `error` is populated. */
type ClientResult<T> = { data?: T; error?: unknown };

async function getOrThrow<T>(url: string, parseAs: "blob" | "arrayBuffer") {
  const response = (await apiClient.get({
    url,
    parseAs,
    security: [{ scheme: "bearer", type: "http" }],
  })) as ClientResult<T>;

  if (response.error) {
    throw new Error(`Failed to fetch ${url}: ${JSON.stringify(response.error)}`);
  }
  if (!response.data) {
    // A 2xx with no body means the proxy returned nothing usable. Failing here
    // beats handing a viewer an empty document, which renders as a blank page
    // with no indication anything went wrong.
    throw new Error(`Failed to fetch ${url}: empty response`);
  }
  return response.data;
}

/**
 * Fetch a proxied blob and return an object URL the viewer can render.
 *
 * Pass `mimeType` when the consumer keys off the blob's type — `docx-preview`
 * and the PDF viewer both do, and the proxy's `Content-Type` doesn't reliably
 * survive the round-trip through the fetch layer.
 *
 * The caller owns the returned URL and should `URL.revokeObjectURL` it when the
 * viewer unmounts; an object URL pins its blob in memory for the life of the
 * document otherwise.
 */
export async function fetchBlobObjectUrl(
  url: string,
  mimeType?: string
): Promise<string> {
  const blob = await getOrThrow<Blob>(url, "blob");
  return URL.createObjectURL(mimeType ? new Blob([blob], { type: mimeType }) : blob);
}

/**
 * Fetch a proxied blob as an ArrayBuffer — what the docx editor loads from.
 */
export async function fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
  return getOrThrow<ArrayBuffer>(url, "arrayBuffer");
}

/**
 * Save a proxied blob to the user's machine under `filename`.
 *
 * Replaces the `<a href={sasUrl} download>` pattern, which can't work against
 * an authenticated endpoint. The object URL is revoked immediately after the
 * synthetic click — the browser has already taken its own reference to the
 * blob by then, so the download completes.
 */
export async function downloadAuthenticatedFile(
  url: string,
  filename: string
): Promise<void> {
  const objectUrl = await fetchBlobObjectUrl(url);

  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noopener";
  // Appended to the DOM before clicking, and revoked on a delay rather than in
  // the same tick: a detached anchor and a synchronous revoke both cancel the
  // download before it starts in Safari/Firefox. Same shape as the existing
  // report-PDF download in `hooks/mutations/use-templates.ts`.
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
}
